import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import './AuthPages.css';

/**
 * User lands here from the password-reset email (redirect URL must include /reset-password
 * in Supabase Dashboard → Authentication → URL configuration).
 */
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ready, setReady] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let recovered = false;
    const markReady = () => {
      recovered = true;
      setReady(true);
      setTimedOut(false);
    };

    if (typeof window !== 'undefined' && window.location.hash.includes('type=recovery')) {
      markReady();
    }

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        markReady();
      }
    });

    const timer = window.setTimeout(() => {
      if (!recovered) setTimedOut(true);
    }, 5000);

    return () => {
      window.clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!confirmPassword) {
      setError('Please confirm your password.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      await supabase.auth.signOut();
      navigate('/login', {
        replace: true,
        state: { flash: 'Your password was updated. Log in with your new password.' }
      });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (timedOut && !ready) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Link issue</h1>
          <p className="auth-subtitle">
            This reset link may be invalid or expired. Request a new one, and confirm{' '}
            <code className="auth-code-inline">/reset-password</code> is listed under Redirect URLs in
            your Supabase project (Authentication → URL configuration).
          </p>
          <p className="auth-footer auth-footer-spaced-top">
            <Link to="/forgot-password">Request new reset link</Link>
            {' · '}
            <Link to="/login">Log in</Link>
          </p>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card-narrow">
          <p className="auth-loading-text">Verifying your reset link…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">Choose a new password</h1>
        <p className="auth-subtitle">Enter a new password for your account.</p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="auth-message error" role="alert">
              {error}
            </div>
          )}
          <div className="auth-field">
            <label htmlFor="reset-password">New password</label>
            <input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              aria-required="true"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
            />
          </div>
          <div className="auth-field">
            <label htmlFor="reset-password-confirm">Confirm new password</label>
            <input
              id="reset-password-confirm"
              type="password"
              autoComplete="new-password"
              aria-required="true"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
            />
          </div>
          <button type="submit" className="auth-submit" disabled={submitting}>
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </form>

        <p className="auth-footer">
          <Link to="/login">Back to log in</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
